import React, { useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Spinner
} from 'reactstrap'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

const LocaleSuggestions = ({
  localeSuggestions = [],
  onApply,
  onApplyAll,
  bulkUpdateStatus = 'idle'
}) => {
  const { t } = useTranslation('ProjectsPage')
  const [expandedPatterns, setExpandedPatterns] = useState({})
  const [applyingPattern, setApplyingPattern] = useState(null)

  if (!localeSuggestions || localeSuggestions.length === 0) {
    return null
  }

  const toggleExpand = (pattern) => {
    setExpandedPatterns((prev) => ({
      ...prev,
      [pattern]: !prev[pattern]
    }))
  }

  const handleApply = (suggestion) => {
    setApplyingPattern(suggestion.path_pattern)
    onApply(suggestion.path_pattern, suggestion.locale)
  }

  const handleApplyAll = () => {
    setApplyingPattern('all')
    onApplyAll(localeSuggestions)
  }

  const isUpdating = bulkUpdateStatus === 'updating'
  const totalCount = localeSuggestions.reduce((sum, s) => sum + s.count, 0)

  return (
    <Card className="mb-4 locale-suggestions">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <span className="fs-5">🎯</span>
          <span className="fw-semibold">
            {t('ImportIssues.localeSuggestions.title')}
          </span>
          <Badge color="info" pill>
            {localeSuggestions.length}
            {' '}
            {t('ImportIssues.localeSuggestions.patterns')}
          </Badge>
          <Badge color="secondary" pill>
            {totalCount.toLocaleString()}
            {' '}
            {t('ImportIssues.localeSuggestions.strings')}
          </Badge>
        </div>
        <Button
          color="success"
          disabled={isUpdating}
          onClick={handleApplyAll}
          size="sm"
        >
          {isUpdating && applyingPattern === 'all' ? (
            <Spinner size="sm" className="me-1" />
          ) : null}
          {t('ImportIssues.localeSuggestions.applyAll')}
        </Button>
      </CardHeader>
      <CardBody className="p-0">
        {localeSuggestions.map((suggestion, index) => (
          <div
            key={suggestion.path_pattern}
            className={`locale-suggestion-item p-3 ${index < localeSuggestions.length - 1 ? 'border-bottom' : ''}`}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <span>📁</span>
                <code className="bg-light px-2 py-1 rounded">
                  {suggestion.path_pattern}
                </code>
                <Badge color="secondary">
                  {suggestion.count.toLocaleString()}
                  {' '}
                  {t('ImportIssues.localeSuggestions.strings')}
                </Badge>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">
                  {t('ImportIssues.localeSuggestions.suggested')}
                  :
                </span>
                <Badge color="primary" className="fs-6">
                  {suggestion.locale}
                </Badge>
                <Button
                  color="outline-primary"
                  disabled={isUpdating}
                  onClick={() => handleApply(suggestion)}
                  size="sm"
                >
                  {isUpdating && applyingPattern === suggestion.path_pattern ? (
                    <Spinner size="sm" className="me-1" />
                  ) : null}
                  {t('ImportIssues.localeSuggestions.apply')}
                </Button>
                <Button
                  color="link"
                  onClick={() => toggleExpand(suggestion.path_pattern)}
                  size="sm"
                >
                  {expandedPatterns[suggestion.path_pattern]
                    ? t('ImportIssues.localeSuggestions.hide')
                    : t('ImportIssues.localeSuggestions.preview')}
                </Button>
              </div>
            </div>
            <Collapse isOpen={expandedPatterns[suggestion.path_pattern]}>
              <Alert color="light" className="mt-2 mb-0">
                <small className="text-muted">
                  {t('ImportIssues.localeSuggestions.previewText', {
                    count: suggestion.count,
                    pattern: suggestion.path_pattern,
                    locale: suggestion.locale
                  })}
                </small>
              </Alert>
            </Collapse>
          </div>
        ))}
      </CardBody>
    </Card>
  )
}

LocaleSuggestions.propTypes = {
  bulkUpdateStatus: PropTypes.string,
  localeSuggestions: PropTypes.arrayOf(PropTypes.shape({
    count: PropTypes.number.isRequired,
    locale: PropTypes.string.isRequired,
    path_pattern: PropTypes.string.isRequired
  })),
  onApply: PropTypes.func.isRequired,
  onApplyAll: PropTypes.func.isRequired
}

export default LocaleSuggestions
